/**
 * Главный экран кабинета: включатель витрины и разделы.
 *
 * Витрину нельзя включить, пока нет товара, зоны и рабочего дня, — это решает
 * сервер. Здесь важно другое: человек должен видеть, ЧЕГО не хватает, до того
 * как нажмёт и получит отказ. Поэтому чек-лист стоит выше переключателя, а не
 * прячется в сообщение об ошибке.
 */
import { useState } from 'react';
import type { CurrentUser, Vendor } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { secondaryButton } from './courierUi';
import { EmptyState, SectionRow, Toggle, errorTextStyle, hintStyle } from './vendorUi';

interface Props {
  user: CurrentUser;
  vendor: Vendor | null;
  loadError: string | null;
  onReload: () => void;
  onVendor: (v: Vendor) => void;
  onProducts: () => void;
  onZones: () => void;
  onSchedule: () => void;
  onPayout: () => void;
  onBack: () => void;
}

export function VendorShop({
  user,
  vendor,
  loadError,
  onReload,
  onVendor,
  onProducts,
  onZones,
  onSchedule,
  onPayout,
  onBack,
}: Props) {
  const t = makeT(user.locale);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loadError) {
    return (
      <main className="page">
        <h1 className="h1">{t('shopTitle')}</h1>
        <EmptyState
          tone="error"
          title={t('shopLoadError')}
          text={loadError}
          actionLabel={t('retry')}
          onAction={onReload}
        />
      </main>
    );
  }

  if (!vendor) {
    return (
      <main className="page">
        <h1 className="h1">{t('shopTitle')}</h1>
        {/* Витрину заводит администратор: это незаконченная настройка,
            а не ошибка исполнителя — и текст должен говорить, что делать. */}
        <EmptyState
          title={t('shopNone')}
          text={t('shopNoneHint')}
          actionLabel={t('courierDeliveries')}
          onAction={onBack}
        />
      </main>
    );
  }

  async function toggleActive(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.patch<Vendor>('/api/vendor', { is_active: next });
      onVendor(updated);
      haptic(next ? 'success' : 'warning');
    } catch (err) {
      haptic('error');
      // Сервер называет каждый недостающий раздел отдельным полем — склеиваем
      // их в одну строку: разделы и так перечислены чек-листом выше.
      const message =
        err instanceof ApiError
          ? Object.values(err.fields ?? {}).join('. ') || err.message
          : t('actionFailed');
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  const checklist = [
    { done: vendor.products_count > 0, text: t('shopNeedProducts') },
    { done: vendor.zones_count > 0, text: t('shopNeedZones') },
    { done: vendor.open_days_count > 0, text: t('shopNeedSchedule') },
  ];

  return (
    <main className="page">
      <h1 className="h1">{vendor.display_name}</h1>

      <section style={{ marginTop: 'var(--s-4)' }}>
        <Toggle
          label={vendor.is_active ? t('shopOpen') : t('shopClosed')}
          hint={vendor.is_active ? undefined : t('shopOpenHint')}
          checked={vendor.is_active}
          disabled={busy}
          onChange={(next) => void toggleActive(next)}
        />
        {error && (
          <p role="alert" style={errorTextStyle}>
            {error}
          </p>
        )}
      </section>

      {!vendor.is_ready && (
        <section style={{ marginTop: 'var(--s-6)' }}>
          <h2 style={{ fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)', margin: '0 0 var(--s-2)' }}>
            {t('shopChecklist')}
          </h2>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--s-2)' }}>
            {checklist.map((item) => (
              <li
                key={item.text}
                style={{
                  display: 'flex',
                  gap: 'var(--s-2)',
                  fontSize: 'var(--fs-sm)',
                  color: item.done ? 'var(--c-text-secondary)' : 'var(--c-text)',
                }}
              >
                {/* Галочка дублируется цветом и положением, но смысл несёт
                    сам символ — цвет здесь вспомогательный */}
                <span aria-hidden="true">{item.done ? '✓' : '•'}</span>
                <span style={{ textDecoration: item.done ? 'line-through' : undefined }}>
                  {item.text}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {vendor.is_ready && vendor.is_active && (
        <p style={{ ...hintStyle, marginTop: 'var(--s-4)' }}>{t('shopReady')}</p>
      )}

      <section style={{ marginTop: 'var(--s-6)', display: 'grid', gap: 'var(--s-2)' }}>
        <SectionRow
          title={t('shopSectionProducts')}
          subtitle={`${vendor.products_count} ${t('productsCount')}`}
          warning={vendor.products_count === 0}
          onClick={onProducts}
        />
        <SectionRow
          title={t('shopSectionZones')}
          subtitle={
            vendor.zones_count === 0
              ? t('zonesEmpty')
              : `${vendor.zones_count} ${t('zonesCount')}`
          }
          warning={vendor.zones_count === 0}
          onClick={onZones}
        />
        <SectionRow
          title={t('shopSectionSchedule')}
          subtitle={
            vendor.open_days_count === 0
              ? t('scheduleAllClosed')
              : `${vendor.open_days_count} / 7`
          }
          warning={vendor.open_days_count === 0}
          onClick={onSchedule}
        />
        <SectionRow
          title={t('shopSectionPayout')}
          subtitle={vendor.contact_phone ?? t('payoutPhoneLabel')}
          onClick={onPayout}
        />
      </section>

      <div style={{ marginTop: 'var(--s-6)' }}>
        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('courierDeliveries')}
        </button>
      </div>
    </main>
  );
}
